import type {
  InitialMapState,
  MapArea,
  MapSelection,
  MapWidgetState,
  SearchFilters,
  SelectedMapArea,
} from '@propileya/geo-widget';
import type { SavedAdmunit } from '@/types';

export interface SavedMapFilter {
  admunits?: SavedAdmunit[];
  mapArea?: MapArea | null;
}

const RESERVED_FILTER_KEYS = new Set([
  'admunitIds',
  'mapArea',
  'focusedPropertyId',
  'currentPage',
  'currentPageIndex',
  'itemsPerPage',
  'sorting',
  'zoom',
  'includeStats',
  'skipStats',
]);

export function toMapFilters(request: Record<string, unknown> | undefined): SearchFilters {
  return Object.fromEntries(
    Object.entries(request ?? {}).filter(
      ([key, value]) => !RESERVED_FILTER_KEYS.has(key) && value !== undefined,
    ),
  );
}

export function toMapSelection(saved: SavedMapFilter): MapSelection {
  const geometry = saved.mapArea?.lonLatGeometry;
  if (geometry?.length) return { kind: 'polygon', geometry };
  const areaIds = saved.admunits?.map(({ id }) => id) ?? [];
  return areaIds.length ? { kind: 'areas', areaIds } : { kind: 'none' };
}

export function toInitialMapState(
  saved: SavedMapFilter,
  focusedPropertyId?: string,
): InitialMapState {
  const bottom = saved.mapArea?.lonLatLeftBottom;
  const top = saved.mapArea?.lonLatRightTop;
  return {
    selection: toMapSelection(saved),
    viewport: bottom && top ? { bounds: [bottom, top] } : undefined,
    focusedPropertyId,
  };
}

export function toSavedMapFilter(state: MapWidgetState, previous: SavedMapFilter): SavedMapFilter {
  const names = new Map<string, SelectedMapArea>(
    state.selectedAreas.map((area) => [area.id, area]),
  );
  const previousNames = new Map<string, SavedAdmunit>(
    (previous.admunits ?? []).map((area) => [area.id, area]),
  );

  const admunits: SavedAdmunit[] =
    state.selection.kind === 'areas'
      ? state.selection.areaIds.map((id) => {
          const area = names.get(id);
          const old = previousNames.get(id);
          return {
            id,
            name: area?.title ?? old?.name ?? id,
            parent_name: area?.parentTitle ?? old?.parent_name ?? '',
          };
        })
      : [];

  return {
    admunits,
    mapArea: {
      lonLatLeftBottom: state.viewport.bounds[0],
      lonLatRightTop: state.viewport.bounds[1],
      ...(state.selection.kind === 'polygon' ? { lonLatGeometry: state.selection.geometry } : {}),
    },
  };
}
